import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import { apiPost, ApiError } from "@/lib/apiClient";
import {
  tryCreate,
  tryDelete,
  tryGet,
  tryList,
  tryPaginated,
  tryUpdate,
  toQueryString,
} from "./_shared";

export type PriceBookItem = Tables<"price_book_items">;
export type PriceBookItemInsert = Omit<TablesInsert<"price_book_items">, "company_id">;
export type PriceBookItemUpdate = TablesUpdate<"price_book_items">;

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export const priceBookItemService = {
  get(id: string) {
    return tryGet<PriceBookItem>(`/api/price-book-items/${encodeURIComponent(id)}`);
  },
  getWithRelations(id: string) {
    return tryGet<PriceBookItem & { uom_ref?: { uom: string } | null }>(
      `/api/price-book-items/${encodeURIComponent(id)}?withRelations=true`,
    );
  },
  listByPriceBook(priceBookId: string) {
    return tryList<PriceBookItem>(`/api/price-books/${encodeURIComponent(priceBookId)}/items`);
  },
  listActiveByPriceBook(priceBookId: string) {
    return tryList<PriceBookItem>(
      `/api/price-books/${encodeURIComponent(priceBookId)}/items?activeOnly=true`,
    );
  },
  listByPriceBookFiltered(priceBookId: string, search: string) {
    return tryList<PriceBookItem>(
      `/api/price-books/${encodeURIComponent(priceBookId)}/items${toQueryString({ search })}`,
    );
  },
  listByPriceBookPaginated(
    priceBookId: string,
    pagination: PaginationParams,
    filters?: { search?: string; isActive?: boolean; itemIds?: string[] },
  ) {
    return tryPaginated<PriceBookItem>(
      `/api/price-books/${encodeURIComponent(priceBookId)}/items${toQueryString({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters?.search,
        isActive: filters?.isActive,
        itemIds: filters?.itemIds?.length ? filters.itemIds.join(",") : undefined,
      })}`,
    );
  },
  async countByPriceBooks(priceBookIds: string[]) {
    if (priceBookIds.length === 0) return { data: {}, error: null };
    try {
      const result = await apiPost<{ data: Record<string, number> }>(
        "/api/price-book-items/counts",
        { priceBookIds },
      );
      return { data: result.data, error: null };
    } catch (err) {
      const e = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { data: {} as Record<string, number>, error: e };
    }
  },
  async countAll() {
    try {
      const result = await apiPost<{ data: Record<string, number> }>(
        "/api/price-book-items/counts",
        {},
      );
      return { data: result.data, error: null };
    } catch (err) {
      const e = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { data: {} as Record<string, number>, error: e };
    }
  },
  create(data: PriceBookItemInsert, _context: ServiceContext) {
    return tryCreate<PriceBookItem>("/api/price-book-items", data);
  },
  async createBatch(items: PriceBookItemInsert[], _context: ServiceContext) {
    return tryCreate<PriceBookItem[]>("/api/price-book-items/batch", items);
  },
  update(
    id: string,
    data: PriceBookItemUpdate,
    _snapshot: Record<string, unknown>,
    _parentId: string,
    _context: ServiceContext,
  ) {
    return tryUpdate<PriceBookItem>(`/api/price-book-items/${encodeURIComponent(id)}`, data);
  },
  delete(id: string, _snapshot: Record<string, unknown>, _parentId: string, _context: ServiceContext) {
    return tryDelete(`/api/price-book-items/${encodeURIComponent(id)}`);
  },
  async deleteBatch(ids: string[], priceBookId: string, _context: ServiceContext) {
    try {
      const result = await apiPost<{ success: boolean; changesLogged: boolean; count: number }>(
        "/api/price-book-items/delete-batch",
        { ids, price_book_id: priceBookId },
      );
      return { success: result.success, error: null, changesLogged: result.changesLogged };
    } catch (err) {
      const e = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { success: false, error: e, changesLogged: false };
    }
  },
  async deleteByPriceBook(priceBookId: string, _context: ServiceContext) {
    try {
      const result = await apiPost<{ success: boolean; changesLogged: boolean; count: number }>(
        `/api/price-books/${encodeURIComponent(priceBookId)}/items/delete-all`,
      );
      return { success: result.success, error: null, changesLogged: result.changesLogged };
    } catch (err) {
      const e = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { success: false, error: e, changesLogged: false };
    }
  },
  toggleActive(
    id: string,
    isActive: boolean,
    _previousIsActive: boolean,
    _parentId: string,
    _context: ServiceContext,
  ) {
    return tryUpdate<PriceBookItem>(`/api/price-book-items/${encodeURIComponent(id)}`, {
      is_active: isActive,
    });
  },
};
